import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import {
  getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey,
  useUpdateShipmentApiV1ShipmentsShipmentIdPatch,
} from '#/shared/api/generated/shipment/theCargoShipment'
import { invalidateShipmentHistory } from '#/entities/shipment'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'
import { toast } from '#/shared/ui/sonner'

import { useResolveCustomer } from './use-resolve-customer'
import type { CustomerFormData } from './customer-schema'

interface UseSaveCustomerArgs {
  shipmentId: string | undefined
  customerId: string | undefined
  originalEmail: string | undefined
}

export function useSaveCustomer({
  shipmentId,
  customerId,
  originalEmail,
}: UseSaveCustomerArgs) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const resolveCustomer = useResolveCustomer()
  const updateShipment = useUpdateShipmentApiV1ShipmentsShipmentIdPatch()

  const save = useCallback(
    async (form: CustomerFormData) => {
      if (!shipmentId) return

      try {
        const nextCustomerId = await resolveCustomer.resolve({
          form,
          customerId,
          originalEmail,
        })
        if (nextCustomerId !== customerId) {
          await updateShipment.mutateAsync({
            shipmentId,
            data: { customer_id: nextCustomerId },
          })
        }
        queryClient.invalidateQueries({
          queryKey:
            getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey(shipmentId),
        })
        queryClient.invalidateQueries({ queryKey: ['/api/v1/shipments'] })
        invalidateShipmentHistory(queryClient)
        toast.success(t('shipmentCard.updateSuccess'))
        return true
      } catch (error) {
        toast.error(getApiErrorDetail(error) ?? t('shipmentCard.updateError'))
        return false
      }
    },
    [
      shipmentId,
      customerId,
      originalEmail,
      resolveCustomer,
      updateShipment,
      queryClient,
      t,
    ],
  )

  return {
    save,
    isSaving: resolveCustomer.isResolving || updateShipment.isPending,
  }
}
